import React, { useState, forwardRef, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import RollingNumber from '../Loaders/RollingNumber';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';

const BuyIcon = 'https://assets.askhippo.co.za/Icons/common/Union.svg';

const MobileProviderTileComponent = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      bestDealContentReady = true,
      wasLoadingOnMount = useRef(!bestDealContentReady),
    },
    ref
  ) => {
    const { FriendlyName, LogoURL, Amount } = data;

    const [isExpanded, setIsExpanded] = useState(false);

    const isBestDealLoading = data.bestDeal && !bestDealContentReady;

    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      return onBuyOnline(evt, { action: 'buy-online', data });
    };
    return (
      <Box
        ref={ref}
        sx={{
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          margin: { xs: '0 16px 16px 16px', sm: '0 0 16px 0' },
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '1px solid #2DC8CB' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          position: 'relative',
        }}
      >
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0, // makes it stretch across the box’s full width
              height: '40px',
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // center the text horizontally
              boxSizing: 'border-box',
              zIndex: 1, // ensure it stays above the rest
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.25rem', // 28px
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              {bestDealContentReady ? (
                'Your Best Deal!'
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box',
            mt: data.bestDeal && initialLoadDone ? '40px' : 0,
          }}
        >
          {/* Expand/Collapse Button */}
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              position: 'absolute',
              top: `${data.bestDeal && initialLoadDone ? 71 : 20}px`,
              right: '16px',
              width: '48px',
              height: '48px',
              padding: '8px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{
                fontWeight: 500,
                fontSize: '1.5rem', // Same size as before
                color: '#000',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
              }}
            >
              keyboard_arrow_down
            </span>
          </Button>

          {/* Logo / Image */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={LogoURL}
                alt={FriendlyName}
                style={{
                  maxHeight: '48px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* Provider Name */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                mt: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1rem', // 16px
                color: '#1E354E',
              }}
            >
              {data.BrandName.replace('.co.za', '')}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Content */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '0.875rem', // 14px
                color: '#1E354E',
              }}
            >
              Personal Loan
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Divider */}
          {data.bestDeal && initialLoadDone && (
            <Box
              sx={{
                mt: '12px',
                width: '100%',
                height: '1px',
                backgroundColor: '#E0E0E0',
              }}
            />
          )}

          {/* Additional Benefits */}
          {initialLoadDone && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <SkeletonLoaderWrapper loading={isBestDealLoading}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontSize: '0.8125rem',
                      color: '#1E354E',
                    }}
                  >
                    Loan Term: <strong>{data.Term} Months Max</strong>
                  </Typography>
                </SkeletonLoaderWrapper>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <SkeletonLoaderWrapper loading={isBestDealLoading}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontSize: '0.8125rem',
                      color: '#1E354E',
                    }}
                  >
                    Interest Amount: estimated{' '}
                    <strong>{data.InterestRate}%</strong>
                  </Typography>
                </SkeletonLoaderWrapper>
              </Box>
            </Box>
          )}

          {/* Divider */}
          <Box
            sx={{
              mt: '12px',
              width: '100%',
              height: '1px',
              backgroundColor: '#E0E0E0',
            }}
          />

          {/* Additional Info Pill */}
          {initialLoadDone &&
            Array.isArray(data.RetailChips) &&
            data.RetailChips.length > 0 && (
              <Box
                sx={{
                  mt: '12px',
                  backgroundColor: data.RetailChipColor,
                  borderRadius: '4px',
                  px: '0px',
                  py: '0px',
                  display: 'inline-block',
                }}
              >
                {data.RetailChips.map((chip, idx) => (
                  <Box key={idx}>
                    <SkeletonLoaderWrapper loading={isBestDealLoading}>
                      <Box
                        key={idx}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#162F3B',
                          backgroundColor: chip.color,
                          letterSpacing: '-0.02em',
                          lineHeight: '1.125rem',
                          borderRadius: '6px',
                          px: '8px',
                          py: '2px',
                          display: 'inline-block',
                        }}
                      >
                        {chip.text}
                      </Box>
                    </SkeletonLoaderWrapper>
                  </Box>
                ))}
              </Box>
            )}

          {/* Bottom Row: Amount on the left, CTA button on the right */}
          <Box
            sx={{
              mt:
                initialLoadDone && data.RetailChips.length > 0 ? '0px' : '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end', // align bottom edges
              width: '100%',
            }}
          >
            {/* Amount */}
            <Box>
              <SkeletonLoaderWrapper loading={isBestDealLoading}>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    color: '#A4ACB0',
                    mb: '8px',
                  }}
                >
                  Loan Amount
                </Typography>
              </SkeletonLoaderWrapper>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                }}
              >
                <RollingNumber
                  value={Amount}
                  prefix={{
                    text: 'R',
                  }}
                  digitSx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '2rem',
                    color: '#162F3B',
                    letterSpacing: '-0.02em',
                  }}
                  wrapperSx={{
                    mt: '-8px',
                  }}
                  extraLoops={3}
                  playSignal={
                    data.bestDeal && wasLoadingOnMount.current ? 1 : 0
                  }
                  animate={data.bestDeal && wasLoadingOnMount.current}
                />
              </Box>
            </Box>

            {/* CTA Button (bottom aligned with Excess) */}
            <Button
              onClick={
                data.OnlineAmount && data.OnlineAmount < data.Amount
                  ? handleBuyOnline
                  : handleSelectQuote
              }
              sx={{
                height: '40px',
                backgroundColor: '#FF0074',
                borderRadius: '18px',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: '16px',
                py: '8px',
                '&:hover': { backgroundColor: '#e60067' },
                ...(isBestDealLoading && {
                  backgroundColor: '#D5D5D5 !important',
                  borderColor: '#D5D5D5 !important',
                  color: '#7A7A7A !important',
                  cursor: 'not-allowed',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#D5D5D5 !important',
                  },
                }),
              }}
            >
              {initialLoadDone &&
              data.OnlineAmount &&
              data.OnlineAmount < data.Amount ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#FFFFFF',
                      mr: '4px',
                    }}
                  >
                    Buy
                  </Typography>
                  <Box
                    component="img"
                    src={BuyIcon}
                    alt="BuyOnline"
                    sx={{
                      mx: '4px',
                      mb: '2px',
                      width: '18px',
                      height: '18px',
                    }}
                  />
                </>
              ) : (
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                >
                  double_arrow
                </span>
              )}
            </Button>
          </Box>

          {isExpanded && (
            <Box
              sx={{
                mt: '16px', // space above the expanded section
                display: 'flex',
                flexDirection: 'column',
                gap: '16px', // spacing between sections
              }}
            >
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* Description */}
              {data.Disclaimer && (
                <Box
                  sx={{
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#1E354E',
                      mb: 1,
                    }}
                  >
                    Description
                  </Typography>

                  <Typography
                    component="div"
                    sx={{
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      color: '#1E354E',
                      maxWidth: '707px',
                      width: '100%',
                      wordWrap: 'break-word',
                      pr: '4px',
                      '& ul': { pl: '16px', mt: 0, mb: 0 },
                      '& li': { mb: '4px' },
                      '& p': { m: 0, mb: '8px' },
                    }}
                    dangerouslySetInnerHTML={{ __html: data.Disclaimer }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
MobileProviderTileComponent.displayName = 'MobileProviderTileComponent';
export default MobileProviderTileComponent;
